import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Send, X } from "lucide-react";
import { Api } from "../lib/api.js";

/**
 * "You should see this" — picks one or more other profiles to send a
 * highly-recommended item to. Shows up in their Home "Highly Recommended"
 * row until they watch or dismiss it.
 */
export function RecommendButton({
  itemId,
  fromProfileId,
}: {
  itemId: string;
  fromProfileId: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState<"compose" | "sent">("compose");

  const profilesQ = useQuery({ queryKey: ["profiles"], queryFn: Api.profiles.list });
  const others = (profilesQ.data?.profiles ?? []).filter((p) => p.id !== fromProfileId);

  const send = useMutation({
    mutationFn: () =>
      Api.recsTo.send({
        from_profile_id: fromProfileId,
        to_profile_ids: [...picked],
        item_id: itemId,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setPhase("sent");
      // Auto-dismiss after a beat.
      window.setTimeout(() => {
        setOpen(false);
        setPhase("compose");
        setPicked(new Set());
        setNote("");
      }, 1500);
    },
  });

  function togglePick(id: string): void {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="pill-ghost"
        title="Send this to another profile"
      >
        <Send size={14} /> You should see this
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative z-10 card max-w-md w-full p-6">
            <div className="flex items-baseline justify-between">
              <div className="section-label text-gold">— RECOMMEND —</div>
              <button onClick={() => setOpen(false)} className="pill-ghost p-1" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {phase === "sent" ? (
              <div className="py-10 text-center">
                <div className="font-display text-2xl text-jade">Sent.</div>
                <div className="text-parchment text-sm mt-2">
                  It'll show up under "Highly Recommended" on their home page.
                </div>
              </div>
            ) : (
              <>
                <h2 className="hero-title text-2xl mt-2">Who should see this?</h2>
                <div className="hero-underline mt-3 w-40" />

                {others.length === 0 ? (
                  <div className="text-parchment mt-6 text-sm">
                    No other profiles to send to — make another profile first.
                  </div>
                ) : (
                  <>
                    <ul className="mt-6 space-y-2">
                      {others.map((p) => {
                        const isPicked = picked.has(p.id);
                        return (
                          <li key={p.id}>
                            <button
                              type="button"
                              onClick={() => togglePick(p.id)}
                              aria-pressed={isPicked}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md border transition ${
                                isPicked
                                  ? "border-gold bg-vermilion/20 text-ivory"
                                  : "border-hairline text-parchment hover:border-gold/40"
                              }`}
                            >
                              <span className="forecourt-mark w-8 h-8 text-base">
                                {p.forecourt_mark ?? p.avatar_emoji ?? "★"}
                              </span>
                              <span className="flex-1 text-left font-display tracking-smallcaps">
                                {p.display_name}
                              </span>
                              <span className="text-xs text-gold">{isPicked ? "✓" : ""}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>

                    <label className="block mt-4">
                      <div className="section-label">Note (optional)</div>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={280}
                        placeholder="Why they should watch this…"
                        className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none text-sm resize-none"
                      />
                    </label>

                    {send.isError && (
                      <div className="text-vermilionHi text-sm mt-3">{(send.error as Error).message}</div>
                    )}

                    <div className="mt-5 flex justify-end gap-2">
                      <button className="pill-ghost" onClick={() => setOpen(false)}>Cancel</button>
                      <button
                        className="pill"
                        onClick={() => send.mutate()}
                        disabled={picked.size === 0 || send.isPending}
                      >
                        <Send size={14} />
                        Send to {picked.size || "..."}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
